import type {ReactElement} from 'react';
import React from 'react';
import styles from './ai-chat-history-drawer.module.scss';
import {Drawer} from '@Components/drawer/drawer';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Direction, DrawerSize} from '@Components/drawer/drawer.types';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {ChatHistoryList} from '@/ai-chat/components/chat-history-list/chat-history-list';
import {ChatHistorySearch} from '@/ai-chat/components/chat-history-search/chat-history-search';
import {AiChatLogo} from '@/ai-chat/components/ai-chat-logo/ai-chat-logo';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {onLogoClick} from '@Libraries/ai-chat.library';
import {ProgressiveBlurOverlay} from '@Components/progressive-blur-overlay/progressive-blur-overlay';

export function AiChatHistoryDrawer(): ReactElement {
  const {actions, state} = useAiChat();
  const isSmall = state.isSmall;
  const historyScrollContainer = React.useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(historyScrollContainer);

  const handleOnClose = (): void => {
    actions.setIsSidebarExpanded(false);
  };

  const getTooltip = (title: string): OverlayChildren => {
    return (
      <div className={`spacing-p-t-1 spacing-p-b-1 spacing-p-l-2 spacing-p-r-2 radius-8 ${styles.tooltipContainer}`}>
        <Text size={TextSize.XSMALL} className={styles.tooltipText} val={title} />
      </div>
    );
  };

  const getHistoryDrawerTrigger = (): ReactElement => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_close_sidebar'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 12]}
      >
        <Icon
          className={`radius-8`}
          onClick={() => {
            actions.setIsSidebarExpanded(false);
          }}
          icon="icon-sidebar"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.GHOST}
          ariaLabel={window.i18next.t('pmwjs_close_sidebar')}
          isUsedAsButton
        />
      </OverlayTrigger>
    );
  };

  const getNewChatButton = (): ReactElement => {
    return (
      <Button
        onClick={() => {
          actions.activateForNewChat();
          actions.setCurrentChatId(undefined);
          actions.setIsSidebarExpanded(!isSmall);
        }}
        icon="icon-plus"
        text={window.i18next.t('pmwjs_new_chat')}
        isFullWidth
        type={Type.PRIMARY}
        customClasses={styles.newChatButton}
        size={Size.SMALL}
      />
    );
  };

  const getDrawerLogoAreaContent = (): ReactElement => {
    if (isSmall) {
      return <Text size={TextSize.SMALL} bold val={window.i18next.t('pmwjs_all_chats')} />;
    }

    return (
      <div className="flexbox gap-1">
        <Icon
          onClick={onLogoClick}
          icon="icon-arrow-left"
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.SQUARE}
          type={IconType.GHOST}
          className="radius-8"
          ariaLabel={window.i18next.t('pmwjs_back')}
          isUsedAsButton
        />
        <AiChatLogo showHamburgerMenu={false} />
      </div>
    );
  };

  const getDrawerTopContent = (): ReactElement => {
    return (
      <>
        <div className={`flex-row-align-center ${styles.sidebarHeader} spacing-p-1 ${isSmall ? 'spacing-p-l-4' : ''}`}>
          {getDrawerLogoAreaContent()}
          {getHistoryDrawerTrigger()}
        </div>
        <div className={`spacing-p-l-4 spacing-p-r-4 flex-v-row gap-3 spacing-p-b-5 ${topShadow ? styles.bottomShadow : ''}`}>
          {getNewChatButton()}
          <ChatHistorySearch />
        </div>
      </>
    );
  };

  const getBottomPaddingClassnameToAccountForQuickTipsButton = (): string => {
    return 'spacing-p-b-12';
  };

  const getQuickTipsButton = (): ReactElement => {
    return (
      <div className={`_full-width spacing-p-l-3 spacing-p-r-3 spacing-p-b-3 spacing-p-t-6 ${styles.quickTipsContainer}`}>
        <ProgressiveBlurOverlay className={styles.progressiveBlurOverlay} />
        <Button onClick={actions.navigateToQuickTips} icon="icon-bulb" text={window.i18next.t('pmwjs_quick_tips')} isFullWidth type={Type.SECONDARY} size={Size.SMALL} />
      </div>
    );
  };

  return (
    <Drawer
      size={DrawerSize.SMALL}
      isOpen={state.isSidebarExpanded}
      onClose={handleOnClose}
      overlay={isSmall}
      direction={Direction.LEFT}
      hasOverflowHidden={false}
      className={`${getBottomPaddingClassnameToAccountForQuickTipsButton()} ${state.currentChatId === undefined ? styles.isHomepageDrawer : ''} ${styles.sideDrawer} flex-shrink-0`}
    >
      {getDrawerTopContent()}
      <ChatHistoryList containerRef={historyScrollContainer} />
      {getQuickTipsButton()}
    </Drawer>
  );
}
